import React, { useMemo } from "react";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import Pagination from "../../ShopServices/Orders/components/Pagination";

import { useBilling } from "../hooks";
import type { BillingItem } from "../../../api/ads";

import { BillingTable } from "./components/BillingTable";

import "./Billing.css";

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialAdvertiserId = useMemo(
    () => searchParams.get("advertiser_id") ?? undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { functions, values, loading } = useBilling(
    useMemo(() => ({ advertiserId: initialAdvertiserId }), [initialAdvertiserId])
  );

  const pageSize = values.params.pageSize ?? 10;
  const currentPage = values.params.pageNum ?? 1;
  const totalCount = values.totalCount ?? 0;

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Billing", active: true },
            ]}
          />
        </div>

        <div className="billing-page-header mb-4" style={{ padding: "0 15px" }}>
          <div className="billing-page-header__left">
            <h1 className="billing-page-title">Billing</h1>
            <p className="billing-page-subtitle">Transaction ledger and invoicing</p>
          </div>
          <div className="billing-page-header__actions">
            {values.params.advertiserId && (
              <button
                type="button"
                className="billing-action-btn"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("advertiser_id");
                  setSearchParams(next, { replace: true });
                  functions.setParams({ advertiserId: undefined });
                }}
              >
                <i className="ph-x billing-action-btn__icon" />
                Clear Filter
              </button>
            )}
          </div>
        </div>

        <Row>
          <Col>
            <Card className="billing-card">
              <Card.Body className="billing-card__body">
                {loading.listLoading ? (
                  <ShimmerLoader type="table" rows={8} />
                ) : values.billing.length > 0 ? (
                  <BillingTable billing={values.billing as BillingItem[]} />
                ) : (
                  <div className="billing-empty">
                    <p className="billing-empty__text">No billing records found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.listLoading && totalCount > 0 && values.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={values.totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(page) => functions.setPageNum(page)}
                onPageSizeChange={(size) => functions.setPageSize(size)}
              />
            )}
          </Col>
        </Row>

        {values.error && (
          <div style={{ padding: "0 15px" }}>
            <Alert variant="danger" dismissible onClose={() => functions.fetchBilling()}>
              {values.error}
            </Alert>
          </div>
        )}
      </Container>
    </div>
  );
};

export default BillingPage;
